import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Link from 'next/link';
import { useSearchApi } from './search-listing/useSearchApi';
import { useWindowedRow } from '../hooks/useWindowedRow';
import { trackError, ErrorSeverity } from '../utils/error-tracking';
import ListingSlider from './listing-slider';
import ShowcaseStripSkeleton from './search-listing/ShowcaseStripSkeleton';
import NotInterestedButton from './not-interested-button';
import {
  isDismissed,
  targetKey,
  type NotInterestedSource,
  type NotInterestedTarget,
} from '../utils/not-interested';
import type { Swiper as SwiperType } from 'swiper';

interface SearchParams {
  categoryGroupId?: number;
  categoryId?: number;
  userId?: string | number | null;
  excludeUserId?: number;
  q?: string;
  minPrice?: number;
  maxPrice?: number;
  itemCondition?: number[] | null;
  shippable?: boolean;
  hasPhoto?: boolean;
  tags?: string[];
  [key: string]: unknown;
}

interface ShowcaseProps {
  title: string;
  searchParams: SearchParams;
  initialListings?: Record<string, unknown>[];
  seeAllUrl?: string;
  categorySlug?: string;
  parentCategorySlug?: string;
  lazy?: boolean;
  minItems?: number;
  itemsPerPage?: number;
  excludeListingId?: number;
  listName?: string;
  setProduct?: (product: Record<string, unknown>) => void;
  tag?: string;
  noMargin?: boolean;
  /**
   * When true, keep the row full "forever": once real paging is exhausted,
   * pivot to tag-graph recommendations of items not already shown.
   */
  recommendForever?: boolean;
  /**
   * Where this row is being shown, which is what turns "Not Interested" on.
   *
   * Omitted means no control: a row that is not about a category or a tag has no
   * axis to record a negative against, and one that is genuinely not a
   * recommendation (a seller's own listings, the items beside a product) is not
   * something a reader is being asked to have an opinion on.
   */
  notInterestedSource?: NotInterestedSource;
  /**
   * What to render instead when this LAZY row loads fewer than `minShown`
   * listings. The product page's seller row uses it: a seller with one or two
   * other listings made a thin row, and one with none made no row at all,
   * leaving two ad frames stacked. Unset keeps the old behaviour (an empty row
   * renders nothing).
   */
  fallback?: React.ReactNode;
  /** The fewest listings that make this row worth showing over `fallback`. Default 1. */
  minShown?: number;
  /**
   * Drop this row's cards while it is far off screen, holding its height.
   *
   * For feeds that append rows without end. Home on an iPhone went white after
   * about a dozen load-mores: every row ever appended stayed mounted with all
   * its cards and decoded photos, the tab ran out of memory, and iOS killed
   * it. That leaves no JS error to report; prod shows the deep sessions ending
   * in a fresh page view of "/" seconds after the last load-more. Parking
   * bounds what is mounted to the rows near the screen, however deep the feed.
   */
  windowed?: boolean;
  /** A line beside the title naming where the row sits ("in Home & Garden"). */
  context?: string;
  /** Ad cards in the row: one every `adsPer` cards from card `adsStart`. Default none. */
  adsPer?: number;
  adsStart?: number;
  /** The page's own response to the row being dismissed or restored (a toast, say). */
  onDismiss?: (dismissed: boolean) => void;
}


/**
 * The line above a showcase's cards: its title, its blurb if it has one, and
 * the way out to the full list.
 *
 * One component because the row is rendered from three places in here — the
 * lazy placeholder, the lazy loaded row, and the plain one — and they were
 * three copies of the same markup. The loading skeleton in
 * search-listing/ShowcaseRowSkeleton.tsx is a fourth, and shares the classes.
 */
const ShowcaseHeading = ({
  title,
  context,
  seeAllLink,
  indent,
  notInterested,
  onDismiss,
}: {
  title: string;
  context?: string;
  seeAllLink?: string;
  indent?: boolean;
  /** Omitted when the row is not about a category or a tag (see `Showcase`). */
  notInterested?: { target: NotInterestedTarget; source: NotInterestedSource };
  onDismiss?: (dismissed: boolean) => void;
}) => (
  <div className={`showcase-heading-row${indent ? ' ml-6' : ''}`}>
    {context ? (
      <div className="showcase-heading-title">
        <h2 className="showcase-heading">{title}</h2>
        <span className="showcase-heading-context">{context}</span>
      </div>
    ) : (
      <h2 className="showcase-heading">{title}</h2>
    )}
    {/* Trailing controls, in reading order: the way deeper in, then the way to
        see less of it. "Not Interested" sits last so it is never the first thing
        a thumb reaches for on the right-hand edge. */}
    {seeAllLink && (
      <Link href={seeAllLink} className="showcase-see-all">
        See All →
      </Link>
    )}
    {notInterested && (
      <NotInterestedButton
        target={notInterested.target}
        source={notInterested.source}
        onChange={({ dismissed }) => onDismiss?.(dismissed)}
        className="showcase-not-interested"
      />
    )}
  </div>
);

/**
 * What dismissing this row would mean, or null if it would mean nothing.
 *
 * A tag row beats a category row when both are available: the tag is the more
 * specific thing on screen, and it is the axis the row was actually built on.
 */
function notInterestedTarget(
  title: string,
  tag: string | undefined,
  searchParams: SearchParams,
): NotInterestedTarget | null {
  // Positive ids only. The browse page's "More ..." row is built with id -1,
  // which names nothing, and a dismissal of nothing is a control that lies.
  const realId = (value: unknown): number | undefined =>
    typeof value === 'number' && value > 0 ? value : undefined;

  const categoryId = realId(searchParams.categoryId);

  if (tag) {
    return categoryId === undefined
      ? { kind: 'tag', label: title, tag }
      : { kind: 'tag', label: title, tag, categoryId };
  }

  const groupId = realId(searchParams.categoryGroupId);

  // Either id is enough. Home-page rows are built from a group and carry no
  // category id at all; a sibling row is the other way round. Requiring the
  // category id would have silently dropped the control from the busiest
  // showcase surface on the site.
  if (categoryId !== undefined || groupId !== undefined) {
    const target: NotInterestedTarget = { kind: 'category', label: title };
    if (categoryId !== undefined) target.categoryId = categoryId;
    if (groupId !== undefined) target.categoryGroupId = groupId;
    return target;
  }

  return null;
}

const Showcase: React.FC<ShowcaseProps> = ({
  title,
  searchParams,
  initialListings = [],
  seeAllUrl,
  categorySlug,
  parentCategorySlug,
  lazy = false,
  minItems = 7,
  itemsPerPage = 10,
  excludeListingId,
  listName = 'showcase',
  setProduct,
  tag,
  noMargin = true,
  recommendForever = false,
  notInterestedSource,
  fallback,
  minShown = 1,
  windowed = false,
  context,
  adsPer = 0,
  adsStart = 999,
  onDismiss,
}) => {
  const { fetchSearchList, fetchTagFallbackList } = useSearchApi();
  const [listings, setListings] = useState<Record<string, unknown>[]>(initialListings);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(initialListings.length >= minItems);
  const [isVisible, setIsVisible] = useState(!lazy);
  const [fetchAttempted, setFetchAttempted] = useState(false); // Track if we've attempted fetch (even if empty)
  const showcaseRef = useRef<HTMLDivElement>(null);
  const swiperRef = useRef<{ swiper: SwiperType } | null>(null);
  const isLoadingRef = useRef(false); // Synchronous loading check to prevent race conditions
  /**
   * Dismissed in THIS page view: the row keeps its heading and drops its cards,
   * so the click has a visible result and the Undo beside it stays reachable.
   */
  const [dismissed, setDismissed] = useState(false);
  const handleDismiss = useCallback((isDismissed: boolean) => {
    setDismissed(isDismissed);
    onDismiss?.(isDismissed);
  }, [onDismiss]);
  /**
   * Dismissed on a PREVIOUS visit, in which case the row is not rendered at all.
   *
   * Resolved in an effect rather than during render because the answer lives in
   * localStorage: the server has no access to it and would render the row, so
   * reading it inline would mean the first client render disagreed with the
   * markup it is hydrating.
   */
  const [previouslyDismissed, setPreviouslyDismissed] = useState(false);

  const dismissTarget = notInterestedSource
    ? notInterestedTarget(title, tag, searchParams)
    : null;
  const dismissKey = dismissTarget ? targetKey(dismissTarget) : null;

  useEffect(() => {
    if (!dismissTarget) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reads localStorage, which is unavailable during SSR; doing it in render would make the first client pass disagree with the server markup
    setPreviouslyDismissed(isDismissed(dismissTarget));
    // `dismissTarget` is rebuilt every render; its identity is the key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissKey]);
  const inFallbackRef = useRef(false); // Whether real paging is exhausted and we're on tag-graph recs
  const fallbackSeenTagsRef = useRef<string[]>([]); // Tags already searched across fallback pages
  // Rows of real paging already taken, the next page's start row. A row's
  // first items often come from another route (home showcases send 7), so
  // `page: 2` at 10 per page started at row 11 and skipped rows 8-10.
  const pagedRef = useRef(initialListings.length);

  // Windowing (see the `windowed` prop and hooks/useWindowedRow).
  const { boxRef: sliderBoxRef, parked, initialSlide: parkedSlide } = useWindowedRow({
    enabled: windowed,
    hasCards: listings.length > 0,
    rowRef: showcaseRef,
    swiperRef,
    keepKey: listName,
  });

  // Memoize searchParams to prevent infinite loops from object reference changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedSearchParams = useMemo(() => searchParams, [
    searchParams.categoryGroupId,
    searchParams.categoryId,
    searchParams.userId,
    searchParams.q,
    searchParams.minPrice,
    searchParams.maxPrice,
    searchParams.itemCondition,
    searchParams.shippable,
    searchParams.hasPhoto,
    searchParams.tags,
  ]);

  // Lazy loading with IntersectionObserver
  useEffect(() => {
    if (!lazy || isVisible) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Any entry, not the first: a busy frame delivers several, oldest first,
        // and [out of view, in view] read as entries[0] never loaded the row.
        // No `!isVisible` check: this effect only subscribes while the row is
        // not visible, so the value this closure sees is always false.
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsVisible(true);
        }
      },
      {
        rootMargin: '200px',
        threshold: 0,
      }
    );

    const currentRef = showcaseRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [lazy, isVisible]);

  // Reset state when search parameters change (for navigating between products)
  // Use a ref to track the current params and only reset when they actually change
  const lastParamsRef = useRef<string>('');
  useEffect(() => {
    if (lazy && (memoizedSearchParams.userId || excludeListingId)) {
      const paramsKey = `${memoizedSearchParams.userId}-${excludeListingId}`;
      if (lastParamsRef.current && lastParamsRef.current !== paramsKey) {
        isLoadingRef.current = false; // Reset loading ref
        inFallbackRef.current = false; // Exit tag-fallback for the new context
        fallbackSeenTagsRef.current = [];
        setFetchAttempted(false); // Reset fetch attempted flag
        setIsVisible(false);
        setPage(1);           // Reset pagination
        pagedRef.current = 0;
        setListings([]);      // Clear old listings
        setHasMore(true);     // Reset hasMore flag
      }
      lastParamsRef.current = paramsKey;
    }
  }, [lazy, memoizedSearchParams, excludeListingId]);

  // Pull the next page of tag-graph recommendations from the items already
  // shown, excluding everything on screen. Keeps hasMore true while the graph
  // still has more to give.
  const loadFallbackPage = useCallback(async (currentListings: Record<string, unknown>[]) => {
    const excludeIds = currentListings
      .map((l) => Number(l.id))
      .filter((n) => Number.isFinite(n));
    const currentTags = Array.from(
      new Set(currentListings.flatMap((l) => (Array.isArray(l.tags) ? (l.tags as string[]) : [])))
    );

    const result = await fetchTagFallbackList({
      currentListTags: currentTags,
      listingIds: excludeIds.slice(-200),
      excludeListingIds: excludeIds.slice(-2000),
      seenTags: fallbackSeenTagsRef.current,
      limit: itemsPerPage,
      categoryGroupId:
        typeof memoizedSearchParams.categoryGroupId === 'number'
          ? memoizedSearchParams.categoryGroupId
          : undefined,
    });

    fallbackSeenTagsRef.current = result.seenTags;

    if (result.listings.length > 0) {
      const existingIds = new Set(currentListings.map((l) => String(l.id)));
      const unique = result.listings.filter(
        (l: Record<string, unknown>) => !existingIds.has(String(l.id))
      );
      if (unique.length > 0) {
        setListings([...currentListings, ...unique]);
      }
    }

    setHasMore(result.hasMore && result.listings.length > 0);
  }, [fetchTagFallbackList, itemsPerPage, memoizedSearchParams]);

  // Load more items
  const loadMore = useCallback(async (_swiper?: SwiperType) => {
    // Use ref for synchronous check to prevent race conditions from rapid onReachEnd events
    if (isLoadingRef.current || isLoading || !hasMore) return;

    isLoadingRef.current = true;
    setIsLoading(true);
    try {
      // Once real paging is exhausted we stay on tag-graph recommendations.
      if (inFallbackRef.current) {
        await loadFallbackPage(listings);
        return;
      }

      const nextPage = page + 1;
      const newListings = await fetchSearchList({
        ...memoizedSearchParams,
        excludeListingId,
        page: nextPage,
        offset: pagedRef.current,
        limit: itemsPerPage,
      });
      // fetchSearchList always resolves to an array (empty on failure).
      pagedRef.current += newListings.length;

      if (newListings.length > 0) {
        // Deduplicate by listing ID
        const existingIds = new Set(listings.map((l) => l.id));
        const uniqueNewListings = newListings.filter(
          (l: Record<string, unknown>) => !existingIds.has(l.id)
        );

        if (uniqueNewListings.length > 0) {
          setListings([...listings, ...uniqueNewListings]);
          setPage(nextPage);
        }

        // If we got fewer items than requested, real paging is done. With
        // recommendForever, keep hasMore true and let the next onReachEnd pull
        // tag-based recs; otherwise stop here.
        if (newListings.length < itemsPerPage) {
          if (recommendForever) {
            inFallbackRef.current = true;
          } else {
            setHasMore(false);
          }
        }
      } else if (recommendForever) {
        // Nothing left from real paging — pivot to recs immediately so the row
        // does not dead-end.
        inFallbackRef.current = true;
        await loadFallbackPage(listings);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      trackError(
        error instanceof Error ? error : String(error),
        ErrorSeverity.ERROR,
        { component: 'Showcase', action: 'loadMore' }
      );
      setHasMore(false);
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
    }
  }, [isLoading, hasMore, page, memoizedSearchParams, itemsPerPage, listings, excludeListingId, fetchSearchList, recommendForever, loadFallbackPage]);

  // Update listings when initialListings or excludeListingId changes
  useEffect(() => {
    if (initialListings.length > 0) {
      const filtered = excludeListingId
        ? initialListings.filter((l) => l.id !== excludeListingId)
        : initialListings;
      // Sync local listings from the initialListings prop. `listings` has
      // multiple writers (loadMore appends, fetchInitialData replaces), so it
      // can't be derived during render; this prop->state reset must stay an effect.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- prop->state sync; listings is stateful with multiple writers
      setListings(filtered);
      pagedRef.current = initialListings.length;
      setHasMore(filtered.length >= minItems);
      // New source data restarts real paging, so leave any prior fallback state.
      inFallbackRef.current = false;
      fallbackSeenTagsRef.current = [];
    }
  }, [initialListings, excludeListingId, minItems]);

  // Filter current listing when excludeListingId changes
  useEffect(() => {
    if (excludeListingId) {
      // Drop the excluded listing from the already-populated, multi-writer
      // `listings` state when excludeListingId changes; a functional updater on
      // existing state, not a synchronous cascading derive.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- filters existing multi-writer state on prop change
      setListings((currentListings) =>
        currentListings.filter((l) => l.id !== excludeListingId)
      );
    }
  }, [excludeListingId]);

  // Fetch initial data when lazy showcase becomes visible
  useEffect(() => {
    const fetchInitialData = async () => {
      // Use ref for synchronous check to prevent race conditions
      // Also check fetchAttempted to prevent re-fetching when results were empty.
      // (A per-params fetch key used to sit here too; it was only ever set beside
      // a fetch whose finally sets fetchAttempted, and cleared beside resetting
      // it, so fetchAttempted already answered every case it did.)
      if (!isVisible || !lazy || listings.length > 0 || isLoadingRef.current || isLoading || fetchAttempted) {
        return;
      }

      isLoadingRef.current = true;
      setIsLoading(true);
      try {
        const newListings = await fetchSearchList({
          ...memoizedSearchParams,
          excludeListingId,
          page: 1,
          limit: itemsPerPage,
        });

        pagedRef.current = newListings.length;
        if (newListings.length > 0) {
          setListings(newListings);
          setHasMore(newListings.length >= itemsPerPage);
        } else {
          setHasMore(false);
        }
      } catch (error) {
        trackError(
          error instanceof Error ? error : String(error),
          ErrorSeverity.ERROR,
          { component: 'Showcase', action: 'fetchInitialData' }
        );
        setHasMore(false);
      } finally {
        isLoadingRef.current = false;
        setFetchAttempted(true); // Mark that we've attempted fetch (even if empty)
        setIsLoading(false);
      }
    };

    void fetchInitialData();
  }, [isVisible, lazy, listings.length, isLoading, fetchAttempted, memoizedSearchParams, itemsPerPage, excludeListingId, fetchSearchList]);

  // Generate see all link
  // Use two-level URL structure when we have a parent category slug (category group + subcategory)
  const seeAllLink = seeAllUrl ||
    (parentCategorySlug && categorySlug ? `/browse/${parentCategorySlug}/${categorySlug}` :
     categorySlug ? `/browse/${categorySlug}` : null);

  // A row the reader already dismissed on an earlier visit is simply not here.
  // Kept out of the earlier returns rather than folded into them because it has
  // to beat the lazy placeholder too: an unmounted IntersectionObserver target
  // would otherwise still occupy the page and still fetch its listings.
  if (previouslyDismissed && !dismissed) return null;

  // Loaded, and too thin to be worth a row: hand the slot to the caller's
  // replacement. Only once the fetch has answered, so it never flashes in
  // ahead of a row that is about to fill.
  if (fallback !== undefined && fetchAttempted && !isLoading && listings.length < minShown) {
    return <>{fallback}</>;
  }

  // A lazy row renders only its IntersectionObserver target until it is seen.
  if (lazy && !isVisible) {
    return <div ref={showcaseRef} className="h-px" />;
  }

  if (listings.length === 0) {
    // Loaded and empty, or a non-lazy row with nothing to show: no heading,
    // no nothing.
    if (!lazy || !isLoading) return null;

    // A lazy row, visible and still fetching: hold the row's shape. This used to
    // fall through to `return null`, so a lazy row was absent right up until it
    // was complete and then pushed everything below it down — on the product
    // page that is an ad frame and the seller's row, both of which moved under
    // the reader. The heading is the real one; only the cards are unknown.
    return (
      <div ref={showcaseRef} className="mb-8">
        <ShowcaseHeading title={title} indent />
        <ShowcaseStripSkeleton flush={noMargin} />
      </div>
    );
  }

  // One row for both: a lazy row indents its heading and keeps the caller's
  // margin; a lazy row only gets here with cards, so isLoading means "more".
  return (
    <div ref={showcaseRef} className="mb-8">
      <ShowcaseHeading
        title={title}
        context={context}
        seeAllLink={seeAllLink}
        indent={lazy}
        notInterested={
          dismissTarget && notInterestedSource
            ? { target: dismissTarget, source: notInterestedSource }
            : undefined
        }
        onDismiss={handleDismiss}
      />
      {dismissed ? null : (
      <div ref={sliderBoxRef} className="relative" data-testid="showcase-row-slider">
        {parked ? null : (
        <ListingSlider
          list={listings as unknown as Parameters<typeof ListingSlider>[0]['list']}
          listName={listName}
          addToList={hasMore ? loadMore : undefined}
          swiperRef={swiperRef}
          tag={tag}
          clickable={true}
          noMargin={lazy ? noMargin : true}
          adsPer={adsPer}
          adsStart={adsStart}
          isLoadingMore={isLoading}
          initialSlide={parkedSlide}
        />
        )}
      </div>
      )}
    </div>
  );
};

export default Showcase;
